import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StripeCardForm } from "@/components/StripeCardForm";
import { CreditCard, CheckCircle2 } from "lucide-react";

const brandLabel = (b) => (b ? b.charAt(0).toUpperCase() + b.slice(1) : "Card");

export default function PaymentMethod() {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/payments/payment-method"); setCard(r.data.card); }
    catch { setCard(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onConfirm = async (paymentMethodId) => {
    try {
      const r = await api.post("/payments/payment-method", { payment_method_id: paymentMethodId });
      setCard(r.data.card);
      setEditing(false);
      toast.success("Payment method updated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update card."); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="payment-method-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Payment method</h1>
        <p className="text-slate-500 mt-1">Add or update the card we use for your BidMatchAI subscription.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-slate-500 text-sm"><div className="h-5 w-5 border-2 border-slate-200 border-t-emerald-700 rounded-full animate-spin" /> Loading…</div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          {card && !editing ? (
            <div className="flex items-center justify-between gap-4" data-testid="current-card">
              <div className="flex items-center gap-4">
                <div className="h-11 w-16 rounded-lg bg-emerald-900 text-white flex items-center justify-center">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2">
                    {brandLabel(card.brand)} •••• {card.last4}
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  </p>
                  <p className="text-sm text-slate-500">Expires {String(card.exp_month).padStart(2, "0")}/{card.exp_year}</p>
                </div>
              </div>
              <button onClick={() => setEditing(true)} data-testid="update-card-button" className="rounded-md px-4 py-2 text-sm font-medium bg-white border border-slate-200 hover:bg-slate-50 transition-colors">
                Update card
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {!card && <p className="text-sm text-slate-500" data-testid="no-card-note">No card on file yet. Add one below to keep your subscription active.</p>}
              <StripeCardForm
                submitLabel={card ? "Save new card" : "Save card"}
                note="Your card is encrypted and processed securely by Stripe. We never store card numbers."
                onConfirm={onConfirm}
              />
              {card && (
                <button onClick={() => setEditing(false)} data-testid="cancel-update-button" className="text-sm text-slate-500 hover:text-slate-700">
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
